(() => {
  let countriesPromise;
  let recommendationPromise;
  const choiceKey = 'nordsign-localization-choice-v1';
  function recommendation() {
    if (!recommendationPromise) {
      const controller = new AbortController();
      const timeout = setTimeout(() => controller.abort(), 2500);
      recommendationPromise = fetch((window.Shopify?.routes?.root || '/') + 'browsing_context_suggestions.json?country[enabled]=true&language[enabled]=true', {credentials:'same-origin', signal:controller.signal})
        .then(response => {if (!response.ok) throw Error('Recommendation unavailable'); return response.json();})
        .catch(() => null).finally(() => clearTimeout(timeout));
    }
    return recommendationPromise;
  }
  function countries() {
    if (!countriesPromise) countriesPromise = fetch('/api/2026-07/graphql.json', {
      method: 'POST', headers: {'Content-Type':'application/json'},
      body: JSON.stringify({query:'query SelectorLocalizations { localization { availableCountries { isoCode availableLanguages { isoCode endonymName } } } }'})
    }).then(async response => { if (!response.ok) throw Error('Localization unavailable'); const result=await response.json(); if (result.errors || !result.data?.localization?.availableCountries?.length) throw Error('Localization unavailable'); return result.data.localization.availableCountries; }).catch(error => { countriesPromise=null; throw error; });
    return countriesPromise;
  }
  const nativeLanguages = {DK:'da', DE:'de', PL:'pl', FR:'fr', SE:'sv', NO:'nb', ES:'es', CH:'de', NL:'nl', IT:'it', FI:'fi', PT:'pt-PT', CZ:'cs'};
  function normalizeLanguage(value) {
    const [language, ...region] = String(value || '').replaceAll('_', '-').split('-');
    return language.toLowerCase() + region.map(part => '-' + part.toUpperCase()).join('');
  }
  function supportedLanguages(country) {
    if (!country) return [];
    const codes = [nativeLanguages[country.isoCode], 'en'].filter((code, index, all) => code && all.indexOf(code) === index);
    return codes.map(code => country.availableLanguages.find(item => normalizeLanguage(item.isoCode) === code)).filter(Boolean);
  }
  let rootRoutingStarted = false;
  async function routeRoot(panel) {
    // Explicit language/market paths are handled by Shopify, without detection.
    if (rootRoutingStarted || location.pathname !== '/' || window.Shopify?.designMode) return;
    if (/(?:bot|crawler|spider|facebookexternalhit|slurp)/i.test(navigator.userAgent || '')) return;
    const params = new URLSearchParams(location.search);
    if (['country', 'country_code', 'locale', 'language_code'].some(key => params.has(key))) return;
    rootRoutingStarted = true;
    try {
      const catalog = await countries();
      let saved;
      try { saved = JSON.parse(localStorage.getItem(choiceKey)); } catch {}
      let country = catalog.find(item => item.isoCode === saved?.country);
      let language = supportedLanguages(country).find(item => normalizeLanguage(item.isoCode) === normalizeLanguage(saved?.language));
      if (!language) {
        const result = await recommendation();
        const code = (result?.detected_values?.country?.handle || result?.suggestions?.[0]?.parts?.country?.handle || '').toUpperCase();
        country = catalog.find(item => item.isoCode === code);
        if (!country) return;
        language = supportedLanguages(country)[0];
      }
      if (!language || document.querySelector('ns-localization-panel dialog[open]') || panel.submitted) return;
      const locale = normalizeLanguage(language.isoCode);
      if (country.isoCode === panel.dataset.country && locale === normalizeLanguage(panel.dataset.language)) return;
      const target = country.isoCode + '/' + locale;
      try {
        if (sessionStorage.getItem('nordsign-root-localization-v1') === target) return;
        sessionStorage.setItem('nordsign-root-localization-v1', target);
      } catch { return; }
      // Reuse Shopify's form endpoint and fields rather than construct market URLs.
      const form = panel.form.cloneNode(false);
      form.removeAttribute('id');
      form.hidden = true;
      for (const input of panel.form.querySelectorAll('input[type="hidden"]')) form.append(input.cloneNode(true));
      const returnTo = form.querySelector('[name="return_to"]');
      if (returnTo) returnTo.value = location.pathname + location.search + location.hash;
      for (const [name, value] of [['country_code', country.isoCode], ['language_code', locale]]) {
        const input = document.createElement('input');
        input.type = 'hidden'; input.name = name; input.value = value; form.append(input);
      }
      document.body.append(form);
      HTMLFormElement.prototype.submit.call(form);
    } catch { /* Keep the rendered page and working manual selector on failure. */ }
  }

  class NsLocalizationPanel extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return; this.initialized=true;
      this.trigger=this.querySelector('.ns-localization-trigger'); this.dialog=this.querySelector('dialog'); this.form=this.querySelector('form'); this.country=this.form.elements.country_code; this.languages=this.querySelector('.ns-localization-languages'); this.status=this.querySelector('[role=status]'); this.apply=this.querySelector('[type=submit]');
      this.trigger.addEventListener('click',()=>this.open());
      routeRoot(this);
      this.querySelector('[data-cancel]').addEventListener('click',()=>this.dialog.close());
      this.dialog.addEventListener('close',()=>{this.trigger.setAttribute('aria-expanded','false');removeTrapFocus();const drawer=this.closest('header-drawer,menu-drawer');if(drawer?.mainDetailsToggle.open)trapFocus(drawer.mainDetailsToggle,this.trigger);else this.trigger.focus();});
      this.dialog.addEventListener('click',event=>{if(event.target===this.dialog){const r=this.dialog.getBoundingClientRect();if(event.clientX<r.left||event.clientX>r.right||event.clientY<r.top||event.clientY>r.bottom)this.dialog.close();}});
      this.country.addEventListener('change',()=>{this.touched=true;this.updateLanguages(undefined, true);});
      this.languages.addEventListener('change',()=>{this.touched=true;});
      this.form.addEventListener('submit',event=>{if(!this.catalog||this.submitted){event.preventDefault();return;}this.submitted=true;this.touched=true;this.apply.disabled=true;
        const language=this.form.querySelector('input[name=language_code]:checked')?.value;
        if(!supportedLanguages(this.catalog.find(item=>item.isoCode===this.country.value)).some(item=>normalizeLanguage(item.isoCode)===language)){event.preventDefault();this.submitted=false;this.updateLanguages(undefined,true);return;}
        try {localStorage.setItem(choiceKey,JSON.stringify({country:this.country.value,language}));} catch {}
        const returnTo=this.form.elements.return_to;
        if(returnTo)returnTo.value=location.pathname+location.search+location.hash;});
    }
    async open() {
      this.form.reset();this.submitted=false;this.touched=false;const generation=this.generation=(this.generation||0)+1;this.country.disabled=true;this.apply.disabled=true;this.status.textContent=this.dataset.loading;
      removeTrapFocus();this.dialog.showModal();this.trigger.setAttribute('aria-expanded','true');trapFocus(this.dialog,this.querySelector('[data-cancel]'));
      try {
        this.catalog=await countries();
        if(generation!==this.generation||!this.dialog.open)return;
        this.country.disabled=false;
        const explicit=this.explicitContext();
        const saved=explicit?null:this.savedChoice();
        if(saved)this.country.value=saved.country;
        this.updateLanguages(saved?.language || this.dataset.language);
        trapFocus(this.dialog,this.country);
        if(!explicit&&!saved)this.suggest(generation);
      }
      catch {this.status.textContent=this.dataset.error;this.apply.disabled=true;this.country.disabled=true;}
    }
    explicitContext() {
      const prefix=(location.pathname.split('/').filter(Boolean)[0]||'').toLowerCase();
      if(this.catalog.some(country=>country.availableLanguages.some(language=>{
        const code=normalizeLanguage(language.isoCode).toLowerCase();
        return prefix===code||prefix===code+'-'+country.isoCode.toLowerCase();
      })))return true;
      const params=new URLSearchParams(location.search);
      if(['country','country_code'].some(key=>this.catalog.some(country=>country.isoCode===params.get(key)?.toUpperCase())))return true;
      return ['locale','language_code'].some(key=>this.catalog.some(country=>country.availableLanguages.some(language=>normalizeLanguage(language.isoCode)===normalizeLanguage(params.get(key)))));
    }
    savedChoice() {
      try {
        const saved=JSON.parse(localStorage.getItem(choiceKey));
        if(typeof saved?.country!=='string'||typeof saved?.language!=='string')return null;
        const country=this.catalog.find(item=>item.isoCode===saved.country);
        return supportedLanguages(country).some(item=>normalizeLanguage(item.isoCode)===normalizeLanguage(saved.language))?saved:null;
      } catch {return null;}
    }
    async suggest(generation) {
      const result=await recommendation();
      if(!result||generation!==this.generation||!this.dialog.open||this.touched||this.submitted)return;
      const parts=result.suggestions?.[0]?.parts;
      const countryCode=(parts?.country?.handle||result.detected_values?.country?.handle||'').toUpperCase();
      const country=this.catalog.find(item=>item.isoCode===countryCode);
      if(!country||![...this.country.options].some(option=>option.value===countryCode))return;
      this.country.value=countryCode;
      this.updateLanguages(undefined, true);
    }
    updateLanguages(preferred, reset = false) {
      const supported=supportedLanguages(this.catalog.find(item=>item.isoCode===this.country.value));
      if(!supported?.length){this.status.textContent=this.dataset.error;this.apply.disabled=true;return;}
      const old=this.form.querySelector('input[name=language_code]:checked');const previous=normalizeLanguage(preferred||(!reset && (old?.value||this.dataset.language))||supported[0].isoCode);const previousName=this.catalog.flatMap(country=>country.availableLanguages).find(language=>normalizeLanguage(language.isoCode)===previous)?.endonymName||previous;
      const selected=supported.find(item=>normalizeLanguage(item.isoCode)===previous)||supported[0];
      this.languages.replaceChildren(...supported.map(language=>{const label=document.createElement('label');label.className='ns-localization-language';const input=document.createElement('input');input.type='radio';input.name='language_code';input.value=normalizeLanguage(language.isoCode);input.required=true;input.checked=language===selected;const name=document.createElement('span');name.lang=input.value;name.textContent=language.endonymName;label.append(input,name);return label;}));
      this.status.textContent=normalizeLanguage(selected.isoCode)===previous?'':this.dataset.fallback.replace('{previous}',previousName).replace('{country}',this.country.selectedOptions[0].textContent).replace('{selected}',selected.endonymName);this.apply.disabled=false;
    }
  }
  if(!customElements.get('ns-localization-panel'))customElements.define('ns-localization-panel',NsLocalizationPanel);
})();
